import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { translate } from '../../../../common/intl';
import {
  ChangeControlOptions,
  DocumentRevision,
  DocumentRevisionStatus,
} from '../../../../state/ducks/documentRevisions/types';
import Text from '../../../components/Text';
import { ButtonGroup } from '../../../components/forms/fields-next';
import { StyledButton } from '../../../components/forms/fields-next/SplitButton/SplitButton.styles';
import {
  BOM_FIELDS_CHANGES_KEY,
  BOM_LINE_CHANGES_KEY,
  cccKeyNamesOrder,
} from '../../../document.revision/utils/helpers';
import { themeNext, withThemeNext } from '../../../layout/theme-next';
import {
  buttonIconStyles,
  DisabledButtonStyles,
  FormDetails,
  Heading,
  iconStyles,
  ListItem,
  ListItemLabel,
  ListItemValue,
  OptionsList,
  StyledPopover,
} from './cancelEditPartButton.styles';

interface Props {
  documentRevision: DocumentRevision
  cancelPartEdit: () => void
}

const CancelEditPartButton: React.FC<Props> = ({
  documentRevision,
  cancelPartEdit,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const changeControlOptions = (documentRevision.changeControlOptions ?? {}) as ChangeControlOptions;
  const formInput = changeControlOptions?.formInput ?? {};
  const schemaMap = changeControlOptions?.schemaMap ?? {};
  const { docId, displayRevision } = changeControlOptions;

  let cccKeyNamesToUse = cccKeyNamesOrder;
  if (formInput[BOM_LINE_CHANGES_KEY] === 'Yes') {
    cccKeyNamesToUse = cccKeyNamesToUse.filter(keyName => keyName !== BOM_FIELDS_CHANGES_KEY);
  }
  const isDisabled = documentRevision.status !== DocumentRevisionStatus.Draft;

  return (<>
    <ButtonGroup kind="white">
      <StyledButton
        id="cancelEditPartButton"
        data-cy="cancelEditPartButton"
        onClick={cancelPartEdit}
        sx={{
          ...(isDisabled && DisabledButtonStyles),
        }}
      >
        <Text message="Cancel Edit" />
      </StyledButton>
      <StyledButton
        id="controlOptionsInfoButton"
        data-cy="controlOptionsInfoButton"
        onClick={handlePopoverOpen}
        sx={{ ...buttonIconStyles(themeNext) }}
      >
        <FontAwesomeIcon
          icon={regular('circle-exclamation')}
          style={iconStyles}
        />
      </StyledButton>
    </ButtonGroup>
    <StyledPopover
      id="controlOptions-popover"
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      onClose={handlePopoverClose}
      disableRestoreFocus
    >
      <Heading variant="h5" component="h5">
        {translate('documentRevision.change.control.config.title')}
        <FormDetails>{docId} rev{displayRevision}</FormDetails>
      </Heading>
      <OptionsList>
        {cccKeyNamesToUse.map((key: string) => (
          schemaMap[key] && (
            <ListItem key={key}>
              <ListItemLabel>{schemaMap[key]}</ListItemLabel> -
              <ListItemValue>
                {key === BOM_FIELDS_CHANGES_KEY ? formInput[key]?.join(' , ') : formInput[key]}
              </ListItemValue>
            </ListItem>
          )
        ))}
      </OptionsList>
    </StyledPopover>
  </>
  );
};

export default withThemeNext(CancelEditPartButton);
